import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import {
  CONTENTFUL_INPUT_TYPES,
  REGEX_PATTERNS,
} from '../../../shared/constants';

import { validateForm } from '../../../utils/forms/formValidation';
import { matchRegex } from '../../../utils/forms/regexHelpers';
import { FORM_LABEL } from '../../../utils/forms/translationKeys';
import { ITranslationItem, languageHelper } from '../../../utils/language';

import Colors from '../../../styles/colors';
import { Body, ButtonLabel, Label, LabelLight } from '../../../styles/generic';

import ErrorMessage from '../../molecules/error-message/ErrorMessage';

interface Props {
  id: string;
  label: string;
  name: string;
  placeholder?: string;
  required: boolean;
  value?: string;
  className?: string;
  onInput?: (value: string) => void;
  errorText?: ITranslationItem;
}

const Textarea = ({
  id,
  label,
  name,
  placeholder,
  required,
  value,
  className,
  onInput,
  errorText,
}: Props) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [errorMessage, setErrorMessage] = useState<ITranslationItem>();
  const { lang } = languageHelper();

  const validateTextarea = (event: React.FormEvent<HTMLTextAreaElement>) => {
    const validationMessage = validateForm(
      (inputRef.current as HTMLTextAreaElement)?.validity,
      label,
      undefined,
      2,
      500,
      CONTENTFUL_INPUT_TYPES.textarea,
      matchRegex(event.currentTarget.value, REGEX_PATTERNS.default),
      undefined,
      errorText
    );

    setErrorMessage(validationMessage);
  };

  return (
    <Label data-testid="textarea" htmlFor={id} className={className}>
      {label}
      {!required ? (
        <StyledLabelLight> {FORM_LABEL.required[lang]}</StyledLabelLight>
      ) : undefined}
      <TextareaContainer>
        <StyledTextarea
          data-testid="input"
          placeholder={placeholder}
          error={errorMessage}
          ref={inputRef}
          id={id}
          name={name}
          required={required}
          defaultValue={value}
          minLength={2}
          maxLength={500}
          onInput={(event) => {
            validateTextarea(event);

            if (onInput) {
              onInput(event.target.value);
            }
          }}
          onInvalid={(event) => {
            event.preventDefault();
            validateTextarea(event);
          }}
        />
      </TextareaContainer>
      {errorMessage && <ErrorMessage>{errorMessage[lang]}</ErrorMessage>}
    </Label>
  );
};

export default Textarea;

const TextareaContainer = styled(Body)`
  font-size: 0.85rem;
  margin-top: 8px;
  position: relative;
  button {
    background: none;
    border: none;
    cursor: pointer;
    position: absolute;
    right: 1rem;
    top: 1.2rem;
  }
`;

const StyledTextarea = styled.textarea<{ error?: ITranslationItem }>`
  ${ButtonLabel}
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.13);
  box-sizing: border-box;
  ${({ error }) =>
    error && `border-bottom:  3px solid ${Colors.abnNegative400}`};
  color: ${Colors.abnBlack};
  font-family: ${({ theme }) => theme.fontRegular};
  height: 84px;
  max-width: 100%;
  padding: 8px;
  width: 100%;

  ${({ error }) => error && `color: ${Colors.abnNegative400}`};

  :disabled {
    border-color: transparent;
  }
`;

const StyledLabelLight = styled(LabelLight)`
  color: ${({ theme }) => theme.colors.abnNeutral300};
  font-family: ${({ theme }) => theme.fontRegular};
`;
